// What? A list view component for displaying document types in a horizontal row layout
// Why? To provide a compact list view for "additional" document types with pagination
// How? Uses JoyUI components in a stacked layout with gradient backgrounds and pagination

import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, Chip, IconButton, Stack, Typography } from "@mui/joy";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { DocumentTypeResponse } from "../../types/documentTypes";
import { generateGradient } from "../../utils/colorUtils";

interface DocumentTypeListProps {
  documentTypes: DocumentTypeResponse[];
  onSelect: (id: number) => void;
}

const ITEMS_PER_PAGE = 6;

const DocumentTypeList: React.FC<DocumentTypeListProps> = ({
  documentTypes,
  onSelect,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    // Reset to first page whenever the input list changes
    setCurrentPage(1);
  }, [documentTypes]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(documentTypes.length / ITEMS_PER_PAGE));
  }, [documentTypes.length]);

  const paginatedDocumentTypes = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return documentTypes.slice(startIndex, endIndex);
  }, [currentPage, documentTypes]);

  return (
    <Stack spacing={2}>
      {/* Document type list */}
      <Stack spacing={2}>
        {paginatedDocumentTypes.map((docType) => {
          const gradientStyle = {
            background: generateGradient(docType.Name),
          };

          return (
            <Card
              key={docType.DocumentTypeID}
              variant="outlined"
              sx={{
                ...gradientStyle,
                cursor: "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                "&:hover": {
                  transform: "translateX(4px)",
                  borderColor: "primary.500",
                  boxShadow: "sm",
                },
              }}
              onClick={() => onSelect(docType.DocumentTypeID)}
            >
              {/* Version chip in top-right corner */}
              <Chip
                size="sm"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 1,
                  opacity: 0.9,
                }}
              >
                v{docType.Version}
              </Chip>

              {/* Content */}
              <Box sx={{ pt: 5, px: 3, pb: 3 }}>
                <Typography
                  level="title-lg"
                  sx={{
                    mb: 0.5,
                    color: "text.primary",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={docType.Name}
                >
                  {docType.Name}
                </Typography>

                {docType.Description && (
                  <Typography
                    level="body-sm"
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                    title={docType.Description}
                  >
                    {docType.Description}
                  </Typography>
                )}
              </Box>
            </Card>
          );
        })}
      </Stack>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <IconButton
            variant="outlined"
            color="neutral"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Page {currentPage} of {totalPages}
          </Typography>

          <IconButton
            variant="outlined"
            color="neutral"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};

export default DocumentTypeList;
